import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import {
  type GuestReviewRatingScale,
  ReviewCardHorizontal,
  ReviewCardVerticalFullText,
} from '@/app/components/hotel-detail/guest-reviews/GuestReviewCard';
import {
  type FilterType,
  GUEST_REVIEWS_PER_PAGE,
  type GuestReviewsFilterCounts,
  type MonthsFilterValue,
  type SortFilterValue,
  type TravellersFilterValue,
  useFetchGuestReviews,
  useGuestReviewsFilterCounts,
} from '@/app/components/hotel-detail/hooks/useFetchGuestReviews';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { Button, Select, Text } from '@lmn/design-system';
import { IconArrowDownLinear, IconCheckLinear } from '@/app/common/icons/libraryIcons';
import type { Brand } from '@/domainLogic/index.ts';
import Image from 'next/image';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled, { css, keyframes } from 'styled-components';
import { useIsMobile } from '../hooks/useMediaSSR';

type ChangeFilterEvent = (type: FilterType, value: string) => void;

const focusVisibleStyles = css`
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  & input:focus-visible {
    outline: none;
    box-shadow: none;
  }

  & *:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

const Separator = styled.hr`
  margin: 16px 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
`;

const RotateAnimation = keyframes`
  100%   {transform: rotate(360deg)}
`;

const ClipAnimation = keyframes`
0%   {clip-path:polygon(50% 50%,0 0,0 0,0 0,0 0,0 0)}
25%  {clip-path:polygon(50% 50%,0 0,100% 0,100% 0,100% 0,100% 0)}
50%  {clip-path:polygon(50% 50%,0 0,100% 0,100% 100%,100% 100%,100% 100%)}
75%  {clip-path:polygon(50% 50%,0 0,100% 0,100% 100%,0 100%,0 100%)}
100% {clip-path:polygon(50% 50%,0 0,100% 0,100% 100%,0 100%,0 0)}
`;

// source: https://cssloaders.github.io/
const CircleLoader = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  position: relative;
  animation: ${RotateAnimation} 1s linear infinite;

  &::before {
    content: '';
    box-sizing: border-box;
    position: absolute;
    inset: 0px;
    border-radius: 50%;
    border: 2px solid ${(p) => p.theme.commons.colors.primary};
    animation: ${ClipAnimation} 2s linear infinite;
  }
`;

const NoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 0;
  row-gap: 24px;
`;

export function ReviewsListModal({ ratingScale }: { ratingScale?: GuestReviewRatingScale }) {
  const { t } = useTranslation();
  const { staticData, commonData, localization } = usePageWrapperData();
  const { isEvoLayout } = useCommonClient();
  const isMobile = useIsMobile(commonData.isMobileDevice);

  const { imageBasePath } = localization;
  const fetchGuestReviews = useFetchGuestReviews();
  const { data: filterCounts, isLoading: isLoadingFilterCounts } = useGuestReviewsFilterCounts();
  const {
    data: hotelReviews,
    isLoading,
    size,
    setSize,
    setSort,
    setTravellerFilter,
    setMonthsFilter,
  } = fetchGuestReviews;

  const scrollableDivRef = useRef<HTMLDivElement>(null);
  const [scrollMoreRef, scrollMoreShown] = useInView({ threshold: 0.02, triggerOnce: false });
  const [isFiltersModalOpen, setFiltersModalOpen] = useState<boolean | undefined>();

  const [sortSelectValue, setSortSelectValue] = useState<SortFilterValue>('date_desc');
  const [travellerSelectValue, setTravellerSelectValue] = useState<TravellersFilterValue>('all');
  const [monthsSelectValue, setMonthsSelectValue] = useState<MonthsFilterValue>('all');

  const someFilterApplied =
    sortSelectValue !== 'date_desc' || travellerSelectValue !== 'all' || monthsSelectValue !== 'all';

  useEffect(() => {
    if (scrollMoreShown) setSize(size + 1);
  }, [scrollMoreShown]);

  const reset = () => {
    setMonthsFilter('all');
    setMonthsSelectValue('all');
    setTravellerFilter('all');
    setTravellerSelectValue('all');
    setSort('date_desc');
    setSortSelectValue('date_desc');
  };

  const onChangeFilter = (filter: FilterType, value: string) => {
    if (filter === 'months') {
      setMonthsFilter(value as MonthsFilterValue);
      setMonthsSelectValue(value as MonthsFilterValue);
    } else if (filter === 'traveller') {
      setTravellerFilter(value as TravellersFilterValue);
      setTravellerSelectValue(value as TravellersFilterValue);
    } else if (filter === 'sort') {
      setSort(value as SortFilterValue);
      setSortSelectValue(value as SortFilterValue);
    }
  };

  const onMoreReviewsClick = () => {
    reset();
  };

  return (
    <div
      ref={scrollableDivRef}
      css={`
        padding: 24px;
      `}
    >
      {isMobile ? (
        <MobileFilters
          onClick={() => setFiltersModalOpen(true)}
          numberOfFiltersApplied={[travellerSelectValue !== 'all', monthsSelectValue !== 'all'].filter((x) => x).length}
        />
      ) : (
        <Filters
          onChangeFilter={onChangeFilter}
          sortSelectValue={sortSelectValue}
          travellerSelectValue={travellerSelectValue}
          monthsSelectValue={monthsSelectValue}
          filterCounts={filterCounts}
          isLoadingFilterCounts={isLoadingFilterCounts}
        />
      )}
      {hotelReviews?.map((hotelReviewPage) =>
        hotelReviewPage.data.reviews?.map((review, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={`${review.name}-${index}`}>
            {isMobile ? (
              <ReviewCardVerticalFullText
                {...review}
                logoUrl={hotelReviewPage.data.sources[review.source]?.logo}
                ratingScale={ratingScale}
              />
            ) : (
              <ReviewCardHorizontal
                {...review}
                logoUrl={hotelReviewPage.data.sources[review.source]?.logo}
                ratingScale={ratingScale}
              />
            )}
            <Separator />
          </Fragment>
        ))
      )}
      {!isLoading && (hotelReviews?.[0]?.data?.totalReviewsCount ?? 0) === 0 ? (
        <NoResultsContainer>
          <Image
            src={`${imageBasePath}${noGuestReviewsImageMap[staticData.domainValues?.brand ?? 'default']}`}
            alt=""
            width={180}
            height={150}
          />
          <div
            css={`
              display: flex;
              flex-direction: column;
              align-items: center;
              row-gap: 8px;
            `}
          >
            <Text variant="Title4">{t('vacation.select.details.guest.reviews.noresult.title')}</Text>
            <Text variant="BodySmall">{t('vacation.select.details.guest.reviews.noresult.subtitle')}</Text>
          </div>
          <Button variant="primary" appearance="outlined" onClick={onMoreReviewsClick}>
            {t('vacation.select.details.guest.reviews.noresult.cta')}
          </Button>
        </NoResultsContainer>
      ) : null}
      {(hotelReviews?.length ?? 0) * GUEST_REVIEWS_PER_PAGE < (hotelReviews?.[0]?.data?.totalReviewsCount ?? 0) ? (
        <div
          ref={scrollMoreRef}
          css={`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <CircleLoader />
        </div>
      ) : null}
      {isMobile ? (
        <OpenXModal
          isOpen={isFiltersModalOpen ?? false}
          isMobile={true}
          brand={staticData.domainValues?.brand as Brand}
          onClose={() => setFiltersModalOpen(false)}
          onCloseByBrowserHistory={() => setFiltersModalOpen(false)}
          onOpenByBrowserHistory={() => setFiltersModalOpen(true)}
          fullScreen={false}
          id="reviews-filter-modal"
          aria-label={t('vacation.select.details.guest.reviews.mobile.filter.modal.title')}
          bodyBackgroundColor="backgroundPage"
          bodyPadding={0}
          bodyPaddingS={0}
          bodyPaddingXS={0}
          // ModalHeader switches to the Evo header (close button on the right) on Evo pages, whose
          // scroll-driven shadow needs scrollShadows.
          scrollShadows={isEvoLayout}
          header={{
            sticky: true,
            element: (
              <ModalHeader handleClose={() => setFiltersModalOpen(false)}>
                {t('vacation.select.details.guest.reviews.mobile.filter.modal.title')}
              </ModalHeader>
            ),
          }}
          footer={{
            sticky: true,
            element: (
              <MobileFiltersModalFooter
                onResetClick={reset}
                onShowReviewsClick={() => setFiltersModalOpen(false)}
                someFilterApplied={someFilterApplied}
              />
            ),
          }}
        >
          <MobileFiltersModalContainer
            onChangeFilter={onChangeFilter}
            sortSelectValue={sortSelectValue}
            travellerSelectValue={travellerSelectValue}
            monthsSelectValue={monthsSelectValue}
            filterCounts={filterCounts}
            isLoadingFilterCounts={isLoadingFilterCounts}
          />
        </OpenXModal>
      ) : null}
    </div>
  );
}

type FiltersProps = {
  onChangeFilter: ChangeFilterEvent;
  sortSelectValue: SortFilterValue;
  travellerSelectValue: TravellersFilterValue;
  monthsSelectValue: MonthsFilterValue;
  filterCounts: GuestReviewsFilterCounts | undefined;
  isLoadingFilterCounts: boolean;
};

// An option that lists no reviews can't be picked. One left without a count (its request failed)
// still can, as every option could before the counts.
const listsNoReviews = (count: number | undefined) => count === 0;

// A filter is only worth showing with two or more options that list reviews: with a single one, it
// lists the same reviews as "All". It stays out until the counts arrive, rather than show and go.
function isFilterShown<Value extends string>(
  options: { value: Value }[],
  counts: Partial<Record<Value, number>> | undefined,
  isLoadingCounts: boolean
) {
  return (
    !isLoadingCounts && options.filter(({ value }) => value !== 'all' && !listsNoReviews(counts?.[value])).length > 1
  );
}

function Filters(props: FiltersProps) {
  const { t } = useTranslation();
  const {
    onChangeFilter,
    sortSelectValue,
    travellerSelectValue,
    monthsSelectValue,
    filterCounts,
    isLoadingFilterCounts,
  } = props;

  const onChangeSort = (value: string) => {
    onChangeFilter('sort', value as SortFilterValue);
  };

  // The pointer can't reach a greyed-out option, but the keyboard still can.
  const onChangeTraveller = (value: string) => {
    if (listsNoReviews(filterCounts?.traveller[value as TravellersFilterValue])) return;
    onChangeFilter('traveller', value as TravellersFilterValue);
  };

  const onChangeMonths = (value: string) => {
    if (listsNoReviews(filterCounts?.months[value as MonthsFilterValue])) return;
    onChangeFilter('months', value as MonthsFilterValue);
  };

  return (
    <>
      <FiltersContainer>
        <Select
          value={sortSelectValue}
          onChange={onChangeSort}
          label={t('vacation.select.details.guest.reviews.sort.title')}
          name="select_sort"
          enableItemSearch={false}
          options={sortOptions.map((option) => ({ label: t(option.key), value: option.value }))}
          css={focusVisibleStyles}
        />

        {isFilterShown(travellersFilterOptions, filterCounts?.traveller, isLoadingFilterCounts) && (
          <Select
            value={travellerSelectValue}
            onChange={onChangeTraveller}
            label={t('vacation.select.details.guest.reviews.filter.travelers.title')}
            name="select_traveller"
            enableItemSearch={false}
            options={travellersFilterOptions.map((option) => ({
              label: filterOptionLabel(t(option.key), listsNoReviews(filterCounts?.traveller[option.value])),
              value: option.value,
            }))}
            css={css`
              /* Room for all seven traveller types: under the menu's own 264px cap the last one scrolls
                 out of sight. The menu renders in a portal, but it still carries this class. */
              &[data-lmn-select-menu] {
                max-height: none;
              }

              ${unavailableOptionStyles}
              ${focusVisibleStyles}
            `}
          />
        )}
        {isFilterShown(timeFilterOptions, filterCounts?.months, isLoadingFilterCounts) && (
          <Select
            value={monthsSelectValue}
            onChange={onChangeMonths}
            label={t('vacation.select.details.guest.reviews.filter.time.title')}
            name="select_months"
            enableItemSearch={false}
            options={timeFilterOptions.map((option) => ({
              label: filterOptionLabel(t(option.key), listsNoReviews(filterCounts?.months[option.value])),
              value: option.value,
            }))}
            css={css`
              ${unavailableOptionStyles}
              ${focusVisibleStyles}
            `}
          />
        )}
      </FiltersContainer>
      <Separator />
    </>
  );
}

const UnavailableOptionLabel = styled.span`
  color: ${({ theme }) => theme.kallisto.color.text.static.textDisabled};
`;

const filterOptionLabel = (label: string, unavailable: boolean) =>
  unavailable ? <UnavailableOptionLabel data-unavailable-option="">{label}</UnavailableOptionLabel> : label;

// The design-system Select has no disabled option, so the greyed-out rows are kept out of the
// pointer's reach: no hover, no pick. The menu renders in a portal but still carries this class.
const unavailableOptionStyles = css`
  & [role='option']:has([data-unavailable-option]) {
    pointer-events: none;
    cursor: default;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  column-gap: 16px;
`;

function MobileFilters(props: { onClick: () => void; numberOfFiltersApplied: number }) {
  const { t } = useTranslation();
  const { onClick, numberOfFiltersApplied } = props;
  return (
    <>
      <div
        role="button"
        aria-label="open sort and filters"
        tabIndex={0}
        onClick={onClick}
        onKeyUp={keyUpHandler(onClick)}
        css={css`
          display: flex;
          padding: 8px 16px;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          border-radius: 8px;
          height: 44px;
          background: ${(p) => p.theme.commons.colors.gray50};
        `}
      >
        <div
          css={`
            display: flex;
            align-items: center;
            column-gap: 8px;
          `}
        >
          <span
            css={`
              line-height: 24px;
            `}
          >
            {t('vacation.select.details.guest.reviews.mobile.filter.modal.title')}
          </span>
          {numberOfFiltersApplied ? (
            <span
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                color: white;
                font-size: 12px;
                font-weight: 700;
                line-height: 14px;
                width: 16px;
                height: 16px;
                padding: 0px 4px;
                border-radius: 8px;
                background: ${(p) => p.theme.commons.colors.primary400};
              `}
            >
              {numberOfFiltersApplied}
            </span>
          ) : null}
        </div>
        <IconArrowDownLinear
          css={css`
            color: ${(p) => p.theme.commons.colors.gray400};
            height: 1.25rem;
            width: 1.25rem;
          `}
        />
      </div>
      <Separator />
    </>
  );
}

function MobileFiltersModalContainer(props: FiltersProps) {
  const { t } = useTranslation();
  const {
    onChangeFilter,
    sortSelectValue,
    travellerSelectValue,
    monthsSelectValue,
    filterCounts,
    isLoadingFilterCounts,
  } = props;

  return (
    <div
      css={css`
        display: flex;
        padding: 0px 16px 16px 16px;
        flex-direction: column;
        background-color: ${(p) => p.theme.commons.colors.gray50};
      `}
    >
      <MobileFilterTitle>{t('vacation.select.details.guest.reviews.sort.title')}</MobileFilterTitle>
      <MobileFilterContainer>
        {sortOptions.map((option) => {
          const filterName = t(option.key);
          return (
            <MobileFilter
              key={option.value}
              role="button"
              aria-label={`filter by ${filterName}`}
              tabIndex={0}
              onClick={() => onChangeFilter('sort', option.value)}
              onKeyUp={keyUpHandler(() => onChangeFilter('sort', option.value))}
            >
              <MobileFilterName>{filterName}</MobileFilterName>
              {sortSelectValue === option.value ? <StyledIconCheck /> : null}
            </MobileFilter>
          );
        })}
      </MobileFilterContainer>
      {isFilterShown(travellersFilterOptions, filterCounts?.traveller, isLoadingFilterCounts) && (
        <>
          <MobileFilterTitle>{t('vacation.select.details.guest.reviews.filter.travelers.title')}</MobileFilterTitle>
          <MobileFilterContainer>
            {travellersFilterOptions.map((option) => {
              const filterName = t(option.key);
              const unavailable = listsNoReviews(filterCounts?.traveller[option.value]);
              const select = unavailable ? undefined : () => onChangeFilter('traveller', option.value);
              return (
                <MobileFilter
                  key={option.value}
                  role="button"
                  aria-label={`filter by ${filterName}`}
                  aria-disabled={unavailable || undefined}
                  tabIndex={0}
                  onClick={select}
                  onKeyUp={select && keyUpHandler(select)}
                >
                  <MobileFilterName color={unavailable ? 'textDisabled' : undefined}>{filterName}</MobileFilterName>
                  {travellerSelectValue === option.value ? <StyledIconCheck /> : null}
                </MobileFilter>
              );
            })}
          </MobileFilterContainer>
        </>
      )}
      {isFilterShown(timeFilterOptions, filterCounts?.months, isLoadingFilterCounts) && (
        <>
          <MobileFilterTitle>{t('vacation.select.details.guest.reviews.filter.time.title')}</MobileFilterTitle>
          <MobileFilterContainer>
            {timeFilterOptions.map((option) => {
              const filterName = t(option.key);
              const unavailable = listsNoReviews(filterCounts?.months[option.value]);
              const select = unavailable ? undefined : () => onChangeFilter('months', option.value);
              return (
                <MobileFilter
                  key={option.value}
                  role="button"
                  aria-label={`filter by ${filterName}`}
                  aria-disabled={unavailable || undefined}
                  tabIndex={0}
                  onClick={select}
                  onKeyUp={select && keyUpHandler(select)}
                >
                  <MobileFilterName color={unavailable ? 'textDisabled' : undefined}>{filterName}</MobileFilterName>
                  {monthsSelectValue === option.value ? <StyledIconCheck /> : null}
                </MobileFilter>
              );
            })}
          </MobileFilterContainer>
        </>
      )}
    </div>
  );
}

function MobileFiltersModalFooter(props: {
  onResetClick: () => void;
  onShowReviewsClick: () => void;
  someFilterApplied: boolean;
}) {
  const { t } = useTranslation();
  const { onResetClick, onShowReviewsClick, someFilterApplied } = props;
  const resetAllTitle = t('vacation.select.details.guest.reviews.filter.button.clear');
  const showReviewsTitle = t('vacation.select.details.guest.reviews.filter.button.apply');
  return (
    <div
      css={`
        display: flex;
        column-gap: 16px;
      `}
    >
      <Button
        variant="primary"
        appearance="ghost"
        disabled={!someFilterApplied}
        aria-label={resetAllTitle}
        onClick={onResetClick}
        css={`
          width: 50%;
        `}
      >
        {resetAllTitle}
      </Button>
      <Button
        variant="primary"
        aria-label={showReviewsTitle}
        onClick={onShowReviewsClick}
        css={`
          width: 50%;
        `}
      >
        {showReviewsTitle}
      </Button>
    </div>
  );
}

const MobileFilter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 16px;
  padding: 12px 16px;
`;

const MobileFilterContainer = styled.div`
  display: flex;
  padding: 8px 0px;
  flex-direction: column;
  border-radius: 8px;
  background-color: white;
`;

const MobileFilterTitle = styled(Text).attrs({ variant: 'Title3' })`
  padding: 16px 0 12px 0;
`;

const MobileFilterName = styled(Text).attrs({ variant: 'Body' })``;

const StyledIconCheck = styled(IconCheckLinear)`
  color: ${(p) => p.theme.commons.colors.primary400};
  width: 20px;
  height: 20px;
`;

const sortOptions: { key: string; value: SortFilterValue }[] = [
  { key: 'vacation.select.details.guest.reviews.sort.recent', value: 'date_desc' },
  { key: 'vacation.select.details.guest.reviews.sort.topRated', value: 'rating_desc' },
  { key: 'vacation.select.details.guest.reviews.sort.lowestRated', value: 'rating_asc' },
];

const travellersFilterOptions: { key: string; value: TravellersFilterValue }[] = [
  { key: 'vacation.select.details.guest.reviews.filter.travelers.all', value: 'all' },
  { key: 'vacation.select.details.guest.reviews.filter.travelers.couples', value: 'partner' },
  { key: 'vacation.select.details.guest.reviews.filter.travelers.solo', value: 'self' },
  { key: 'vacation.select.details.guest.reviews.filter.travelers.families', value: 'family' },
  { key: 'vacation.select.details.guest.reviews.filter.travelers.children', value: 'family_with_children' },
  { key: 'vacation.select.details.guest.reviews.filter.travelers.groups', value: 'friends' },
  { key: 'vacation.select.details.guest.reviews.filter.travelers.pets', value: 'pet' },
];

const timeFilterOptions: { key: string; value: MonthsFilterValue }[] = [
  { key: 'vacation.select.details.guest.reviews.filter.time.allseasons', value: 'all' },
  { key: 'vacation.select.details.guest.reviews.filter.time.spring', value: '3,5' },
  { key: 'vacation.select.details.guest.reviews.filter.time.summer', value: '6,8' },
  { key: 'vacation.select.details.guest.reviews.filter.time.autumn', value: '9,11' },
  { key: 'vacation.select.details.guest.reviews.filter.time.winter', value: '12,2' },
];

const noGuestReviewsImageMap: { [key: string]: string } = {
  lastminute: 'no-guest-reviews-found-pink.svg',
  wegde: 'no-guest-reviews-found-orange.svg',
  rumbo: 'no-guest-reviews-found-pink.svg',
  volagratis: 'no-guest-reviews-found-pink.svg',
  default: 'no-guest-reviews-found-pink.svg',
};
